import React, { useState } from "react";
import { Check, AlertTriangle, ZoomIn, ZoomOut, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { A4Preview } from "@/components/negotiation/A4Preview";
import { t } from "@/lib/negotiationLabels";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

export function ReadinessBar({ readiness }) {
  if (!readiness) return null;
  return (
    <div className="px-4 md:px-5 py-3 border-b border-border" data-testid="ws-readiness">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <p className="text-xs font-semibold">
          {t("negotiationProfile.readiness")} <span className="text-athlynx" data-testid="ws-readiness-score">{readiness.overall}%</span>
        </p>
        <div className="flex gap-1" aria-hidden="true">
          {readiness.sections.map((s) => (
            <span key={s.id} title={`${t(`negotiationProfile.steps.${s.id}`)} : ${s.score}%`}
              className={cn("h-1.5 w-5 rounded-full",
                s.status === "complete" ? "bg-success" : s.status === "warning" ? "bg-warning" : "bg-danger/60")} />
          ))}
        </div>
      </div>
      <Progress value={readiness.overall} className="h-1.5" aria-label={`Fiche prête à ${readiness.overall}%`} />
    </div>
  );
}

export function StepNav({ steps, current, readiness, onSelect }) {
  const secById = Object.fromEntries((readiness?.sections || []).map((s) => [s.id, s]));
  return (
    <nav className="px-3 md:px-4 py-2.5 border-b border-border overflow-x-auto" aria-label={tfr("Étapes de la fiche")}>
      <ol className="flex items-center gap-1.5 min-w-max">
        {steps.map((sid, i) => {
          const n = i + 1;
          const sec = secById[sid];
          const complete = sec?.status === "complete";
          const warning = sec?.status === "warning" && n !== current;
          const active = n === current;
          return (
            <li key={sid}>
              <button
                onClick={() => onSelect(n)}
                aria-current={active ? "step" : undefined}
                data-testid={`ws-step-btn-${sid}`}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 h-8 text-xs font-medium border transition-colors whitespace-nowrap",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-athlynx/40",
                  active
                    ? "bg-athlynx text-white border-athlynx"
                    : "border-border text-muted-foreground hover:text-foreground hover:border-athlynx/30"
                )}
              >
                <span className={cn("inline-flex items-center justify-center h-4.5 w-4.5", !active && complete && "text-success", !active && warning && "text-warning")}>
                  {complete && !active ? <Check className="h-3.5 w-3.5" aria-label={tfr("complète")} />
                    : warning ? <AlertTriangle className="h-3.5 w-3.5" aria-label={tfr("incomplète")} />
                      : <span className="tabular-nums">{n}</span>}
                </span>
                {t(`negotiationProfile.steps.${sid}`)}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function StepFooter({ step, total, onPrev, onNext }) {
  return (
    <div className="shrink-0 grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-3 border-t border-border bg-card/95"
      data-testid="ws-step-footer">
      <div>
        {step > 1 && (
          <Button variant="outline" size="sm" onClick={onPrev} className="h-9" data-testid="ws-prev-btn">
            {t("negotiationProfile.actions.prevStep")}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground tabular-nums" aria-live="polite">
        {t("negotiationProfile.stepOf", { a: step, b: total })}
      </p>
      <div className="text-right">
        {step < total && (
          <Button size="sm" onClick={onNext} className="h-9 bg-athlynx hover:bg-athlynx/90 text-white" data-testid="ws-next-btn">
            {t("negotiationProfile.actions.nextStep")}
          </Button>
        )}
      </div>
    </div>
  );
}

export function PreviewPanel({ player, sportLabel, data, statFields, agent, mobileOpen, onMobileClose }) {
  const [zoom, setZoom] = useState(100);
  const [fullscreen, setFullscreen] = useState(false);

  const body = (inDialog) => (
    <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6 bg-black/20">
      <div style={{ width: `${zoom}%`, maxWidth: inDialog ? 900 : undefined }} className="mx-auto transition-[width] duration-200">
        <A4Preview player={player} sportLabel={sportLabel} identity={data.identity || {}} form={data}
          statFields={statFields} agent={agent} sections={data.sections || {}} />
      </div>
    </div>
  );

  const toolbar = (
    <div className="shrink-0 flex items-center justify-between gap-2 px-3.5 py-2 border-b border-border">
      <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{t("negotiationProfile.preview.title")}</p>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.max(50, z - 10))}
          aria-label={t("negotiationProfile.preview.zoomOut")} data-testid="preview-zoom-out">
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="text-xs tabular-nums w-11 text-center" data-testid="preview-zoom-value">{zoom}%</span>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.min(150, z + 10))}
          aria-label={t("negotiationProfile.preview.zoomIn")} data-testid="preview-zoom-in">
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setFullscreen(true)}
          aria-label={t("negotiationProfile.preview.fullscreen")} data-testid="preview-fullscreen-btn">
          <Maximize2 className="h-4 w-4" />
        </Button>
        {mobileOpen !== undefined && mobileOpen && (
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 xl:hidden" onClick={onMobileClose}
            aria-label={t("negotiationProfile.preview.close")} data-testid="preview-mobile-close">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div
        className={cn(
          "flex-col min-w-0 min-h-0 rounded-2xl border border-border bg-card overflow-hidden",
          mobileOpen ? "flex fixed inset-0 z-[60] rounded-none xl:static xl:rounded-2xl" : "hidden xl:flex"
        )}
        data-testid="ws-preview-panel"
        data-mobile-open={mobileOpen ? "true" : "false"}
      >
        {toolbar}
        {body(false)}
      </div>
      <Dialog open={fullscreen} onOpenChange={setFullscreen}>
        <DialogContent className="max-w-[min(96vw,980px)] h-[92vh] p-0 flex flex-col gap-0" data-testid="preview-fullscreen-dialog">
          <DialogTitle className="sr-only">{t("negotiationProfile.preview.title")}</DialogTitle>
          <div className="shrink-0 flex items-center justify-end gap-1 px-3 py-2 border-b border-border">
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.max(50, z - 10))} aria-label={t("negotiationProfile.preview.zoomOut")}>
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-xs tabular-nums w-11 text-center">{zoom}%</span>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.min(150, z + 10))} aria-label={t("negotiationProfile.preview.zoomIn")}>
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
          {body(true)}
        </DialogContent>
      </Dialog>
    </>
  );
}
